import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import DataTestAttrsType from 'test/lib/cypress/DataTestAttrsType';
import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import Avatar from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import Clipboard from 'ui-library/Clipboard';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

const DROPDOWN_MENU_ITEM_SHAPE = {
  avatarUrl: PropTypes.string,
  clipboardData: PropTypes.string,
  dataMethod: PropTypes.string,
  disabled: PropTypes.bool,
  dropLeft: PropTypes.bool,
  dropup: PropTypes.bool,
  extraClasses: PropTypes.string,
  extraIconClasses: PropTypes.string,
  handleOnClick: PropTypes.func,
  icon: PropTypes.string,
  isHeader: PropTypes.bool,
  link: PropTypes.string,
  generateLinkFromSubmenu: PropTypes.bool,
  name: PropTypes.node.isRequired,
  visible: PropTypes.bool,
};

export const DROPDOWN_MENU_ITEM = PropTypes.shape({
  ...DROPDOWN_MENU_ITEM_SHAPE,
  submenu: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape(DROPDOWN_MENU_ITEM_SHAPE)).isRequired),
});

enum MenuStyle {
  COMPACT = 'compact',
}

interface Props {
  className?: string;
  extraElement?: JSX.Element;
  items: Array<Array<LegacyDropdownMenuItem>>;
  menuStyle?: MenuStyle;
  positionAboveWindowBottom?: boolean;
  dropLeft: boolean;
  dropup?: boolean;
  dataTestAttrs?: DataTestAttrs;
  expandToFit?: boolean;
}

/**
 * Deprecated dropdown. Use `Menu` for new components instead.
 * @deprecated - use ui-library/menus/Menu.tsx instead
 */
class LegacyDropdownMenu extends React.Component<Props, {}> {
  static propTypes = {
    className: PropTypes.string,
    extraElement: PropTypes.element,
    items: PropTypes.arrayOf(PropTypes.arrayOf(DROPDOWN_MENU_ITEM).isRequired).isRequired,
    // eslint-disable-next-line no-restricted-syntax
    menuStyle: PropTypes.oneOf(_.values(MenuStyle)),
    positionAboveWindowBottom: PropTypes.bool,
    dropLeft: PropTypes.bool.isRequired,
    dropup: PropTypes.bool,
    dataTestAttrs: DataTestAttrsType,
  };

  static defaultProps = {
    dropLeft: false,
    dropup: false,
  };

  static STYLES = MenuStyle;

  topRef = React.createRef<HTMLDivElement>();
  menuRef = React.createRef<HTMLUListElement>();

  componentDidMount(): void {
    this._positionAboveWindowBottom();
    window.addEventListener('resize', this._positionAboveWindowBottom.bind(this));
  }

  componentDidUpdate(): void {
    this._positionAboveWindowBottom();
  }

  componentWillUnmount(): void {
    window.removeEventListener('resize', this._positionAboveWindowBottom.bind(this));
  }

  _positionAboveWindowBottom(): void {
    if (!this.topRef.current || !this.menuRef.current) {
      return;
    }

    const windowHeight = $(window).height();
    const globalCreateMenuHeight = $(this.menuRef.current).height();

    // Determine if the height of our global create menu is bigger/smaller than the window.
    if (globalCreateMenuHeight < windowHeight) {
      // If the global create menu fits within the window, position it above the window bottom.
      const alignedTop =
        $(window).height() - $(this.topRef.current).offset()!.top - $(this.menuRef.current).height() - 5;
      const top = Math.min(0, alignedTop);

      // Clear out any css values that may have been set by the else case below.
      $(this.menuRef.current).css('top', top);
      $(this.menuRef.current).css('left', '');
      $(this.menuRef.current).css('transform', '');
    } else {
      // At a certain small viewport size, our whole app becomes unusable. This is a best-effort to keep
      // the menu completely visiable and usable in very small viewports.
      $(this.menuRef.current).css('transform', 'scale(0.8)');
      $(this.menuRef.current).css('top', '-300px');
      $(this.menuRef.current).css('left', '30px');
    }
  }

  _renderMenuItem(item: LegacyDropdownMenuItem, itemIndex: number): JSX.Element | null {
    let itemIsHidden = item.visible === false;
    if (
      !item.link &&
      item.submenu != null &&
      item.submenu.every((s) => s.every((i) => i.visible === false))
    ) {
      itemIsHidden = true;
    }
    let itemHref = item.link;
    if (!itemHref && item.generateLinkFromSubmenu) {
      itemHref = item.submenu
        ?.flat()
        .find((subMenuItem) => subMenuItem && !(subMenuItem.visible === false) && subMenuItem.link)?.link;
    }

    const itemLink = (
      <a
        // tabIndex={0} makes this element focusable, which is needed for cypress tests, which use cy.focus()
        // since there is no cy.hover()
        tabIndex={0}
        className={classNames('navDropdownMenu-item', item.extraClasses, {
          'navDropdownMenu-item--compact': this.props.menuStyle === MenuStyle.COMPACT,
          'navDropdownMenu-item--noIcon': item.icon == null && item.avatarUrl == null,
          'navDropdownMenu-item--header': item.isHeader,
          disabled: item.disabled,
        })}
        {...getTestProps(item.dataTestAttrs)}
        data-method={item.dataMethod}
        href={!item.disabled ? itemHref : undefined}
        onClick={!item.disabled ? item.handleOnClick : undefined}
        {...(!itemHref || itemHref.startsWith('/')
          ? null
          : {
              target: '_blank',
              rel: 'noreferrer noopener',
            })}
      >
        {(() => {
          if (item.avatarUrl != null) {
            // Note: Items with avatar URLs should only be entities.
            return (
              <Avatar
                className="navDropdownMenu-item-avatar"
                entity={{avatar_url: item.avatarUrl, name: item.name as string}}
                size={AvatarSize.XSMALL}
                shape={AvatarShape.SQUARE}
              />
            );
          } else if (item.icon != null) {
            return (
              <BenchlingIcon
                className={classNames('navDropdownMenu-item-icon', item.extraIconClasses)}
                icon={item.icon}
              />
            );
          }
          return null;
        })()}
        <span className="navDropdownMenu-item-text">{item.name}</span>
        {item.submenu != null && <BenchlingIcon icon="chevron" className="navDropdownMenu-item-chevron" />}
      </a>
    );
    return (
      <li
        className={classNames('navDropdownMenu-item-container', {
          hidden: itemIsHidden,
          disabled: item.disabled === true,
          'navDropdownMenu-submenu': item.submenu != null,
        })}
        key={itemIndex}
        {...getTestProps({component: 'LegacyDropdownMenu', element: 'item'})}
      >
        {(() => {
          if (item.clipboardData != null) {
            return <Clipboard value={item.clipboardData}>{itemLink}</Clipboard>;
          } else if (item.tooltipText) {
            return (
              <Tooltip text={item.tooltipText} disableContentSelection={true}>
                {itemLink}
              </Tooltip>
            );
          }
          return itemLink;
        })()}
        {item.submenu != null && (
          <ul
            className={classNames('navDropdownMenu dropdown-menu', {
              'navDropdownMenu--dropUp': item.dropup ?? this.props.dropup,
              'navDropdownMenu--dropLeft': item.dropLeft,
            })}
          >
            {this._renderMenu(item.submenu)}
          </ul>
        )}
      </li>
    );
  }

  _renderMenuSection(
    section: Array<LegacyDropdownMenuItem>,
    sectionIndex: number,
    numSections: number
  ): JSX.Element | null {
    if (section.every((item) => item.visible === false)) {
      return null;
    }
    return (
      <div key={sectionIndex}>
        {section.map((item, itemIndex) => this._renderMenuItem(item, itemIndex))}
        {sectionIndex < numSections - 1 && <li className="divider" />}
      </div>
    );
  }

  _renderMenu(sections: Array<Array<LegacyDropdownMenuItem>>): Array<JSX.Element | null> {
    return sections.map((section, i) => this._renderMenuSection(section, i, sections.length));
  }

  render(): JSX.Element {
    return (
      <>
        {this.props.positionAboveWindowBottom && <div className={css(styles.topRef)} ref={this.topRef} />}
        <ul
          className={classNames(
            'navDropdownMenu dropdown-menu',
            this.props.className,
            this.props.dropLeft && css(styles.dropLeft),
            this.props.expandToFit && css(styles.expandToFit)
          )}
          {...getTestProps(this.props.dataTestAttrs)}
          ref={this.menuRef}
        >
          {this.props.extraElement}
          {this._renderMenu(this.props.items)}
        </ul>
      </>
    );
  }
}

const styles = StyleSheet.create({
  dropLeft: {
    left: 'auto',
    right: '100%',
  },
  topRef: {
    position: 'absolute',
    top: 0,
  },
  expandToFit: {
    width: 'fit-content',
    'white-space': 'nowrap',
  },
});

export default LegacyDropdownMenu;
